import { useState } from "react";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import { useCreateEvent } from "../events/useCreateEvent.js";
import { useEditEvent } from "../events/useEditEvent.js";
import { getCountries, getCities } from "../constants/geography.js";

const VENUE_OPTIONS = [
    "Chasers Nightclub",
    "Inflation",
    "Aura Nightclub",
    "ARQ",
    "Ivy",
    "Jimmy's Rooftop",
    "The Court",
];

function renderFieldError(message) {
    return message ? <p className="event-form-error">{message}</p> : null;
}

function CreateEventForm({ eventToEdit, onCloseModal }) {
    const isEditSession = Boolean(eventToEdit);
    const countries = getCountries();
    const initialCountry = eventToEdit?.country || countries[0] || "";
    const initialCities = getCities(initialCountry);
    const initialCity = eventToEdit?.city || initialCities[0] || "";

    const defaultStartDatetime = (() => {
        if (eventToEdit?.startDatetime) {
            return format(new Date(eventToEdit.startDatetime), "yyyy-MM-dd'T'HH:mm");
        }

        const date = new Date();
        date.setHours(22, 0, 0, 0);
        return format(date, "yyyy-MM-dd'T'HH:mm");
    })();

    const { id: editId, ...editValues } = eventToEdit || {};
    const [selectedCountry, setSelectedCountry] = useState(initialCountry);
    const [selectedCity, setSelectedCity] = useState(initialCity);
    const [venueMode, setVenueMode] = useState(
        eventToEdit?.venue && !VENUE_OPTIONS.includes(eventToEdit.venue)
            ? "custom"
            : "list"
    );

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
        setValue,
        watch,
    } = useForm({
        defaultValues: {
            ...editValues,
            country: initialCountry,
            city: initialCity,
            type: eventToEdit?.type || "Party",
            venue: eventToEdit?.venue || "",
            startDatetime: defaultStartDatetime,
        },
    });

    const currentVenue = watch("venue");
    const cities = getCities(selectedCountry);
    const { isCreating, createEvent } = useCreateEvent();
    const { isEditing, editEvent } = useEditEvent();
    const isWorking = isCreating || isEditing;

    function closeForm() {
        reset();
        onCloseModal?.();
    }

    function onSubmit(data) {
        if (isEditSession) {
            editEvent(
                { newCabinData: data, id: editId },
                { onSuccess: closeForm }
            );
            return;
        }

        createEvent(data, { onSuccess: closeForm });
    }

    function handleCountryChange(event) {
        const nextCountry = event.target.value;
        const nextCity = getCities(nextCountry)[0] || "";

        setSelectedCountry(nextCountry);
        setSelectedCity(nextCity);
        setValue("country", nextCountry, { shouldDirty: true, shouldValidate: true });
        setValue("city", nextCity, { shouldDirty: true, shouldValidate: true });
    }

    function handleCityChange(event) {
        setSelectedCity(event.target.value);
        setValue("city", event.target.value, { shouldDirty: true, shouldValidate: true });
    }

    return (
        <div className="event-form">
            <header className="event-form-header">
                <div>
                    <p className="event-form-eyebrow">
                        {isEditSession ? "Event details" : "New event"}
                    </p>
                    <h2>{isEditSession ? "Edit event" : "Create event"}</h2>
                </div>
                <p>Required fields are marked with an asterisk.</p>
            </header>

            <form onSubmit={handleSubmit(onSubmit)} className="event-form-body">
                <div className="event-form-field event-form-field-wide">
                    <label htmlFor="name">Event name *</label>
                    <input
                        disabled={isWorking}
                        type="text"
                        id="name"
                        autoFocus
                        {...register("name", { required: "Enter an event name." })}
                    />
                    {renderFieldError(errors.name?.message)}
                </div>

                <div className="event-form-field">
                    <label htmlFor="country">Country *</label>
                    <select
                        disabled={isWorking}
                        id="country"
                        value={selectedCountry}
                        {...register("country", { required: "Select a country." })}
                        onChange={handleCountryChange}
                    >
                        {countries.map((country) => (
                            <option key={country} value={country}>{country}</option>
                        ))}
                    </select>
                    {renderFieldError(errors.country?.message)}
                </div>

                <div className="event-form-field">
                    <label htmlFor="city">City *</label>
                    <select
                        disabled={isWorking}
                        id="city"
                        value={selectedCity}
                        {...register("city", { required: "Select a city." })}
                        onChange={handleCityChange}
                    >
                        {cities.map((city) => (
                            <option key={city} value={city}>{city}</option>
                        ))}
                    </select>
                    {renderFieldError(errors.city?.message)}
                </div>

                <div className="event-form-field event-form-field-wide">
                    <div className="event-form-label-row">
                        <label htmlFor="venue">Venue *</label>
                        <div className="event-form-segmented" aria-label="Venue input type">
                            <button
                                type="button"
                                className={venueMode === "list" ? "is-active" : ""}
                                onClick={() => setVenueMode("list")}
                                disabled={isWorking}
                            >
                                Saved
                            </button>
                            <button
                                type="button"
                                className={venueMode === "custom" ? "is-active" : ""}
                                onClick={() => setVenueMode("custom")}
                                disabled={isWorking}
                            >
                                Custom
                            </button>
                        </div>
                    </div>
                    <input
                        type="hidden"
                        {...register("venue", { required: "Select or enter a venue." })}
                    />
                    {venueMode === "list" ? (
                        <select
                            disabled={isWorking}
                            id="venue"
                            value={VENUE_OPTIONS.includes(currentVenue) ? currentVenue : ""}
                            onChange={(event) =>
                                setValue("venue", event.target.value, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                })
                            }
                        >
                            <option value="">Select a venue</option>
                            {VENUE_OPTIONS.map((venue) => (
                                <option key={venue} value={venue}>{venue}</option>
                            ))}
                        </select>
                    ) : (
                        <input
                            disabled={isWorking}
                            type="text"
                            id="venue"
                            value={currentVenue || ""}
                            onChange={(event) =>
                                setValue("venue", event.target.value, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                })
                            }
                            placeholder="Venue name"
                        />
                    )}
                    {renderFieldError(errors.venue?.message)}
                </div>

                <div className="event-form-field event-form-field-wide">
                    <label htmlFor="description">Description *</label>
                    <textarea
                        disabled={isWorking}
                        id="description"
                        rows={3}
                        {...register("description", { required: "Enter a short description." })}
                    />
                    {renderFieldError(errors.description?.message)}
                </div>

                <div className="event-form-field">
                    <label htmlFor="ticketsUrl">Tickets URL *</label>
                    <input
                        disabled={isWorking}
                        type="url"
                        id="ticketsUrl"
                        placeholder="https://"
                        {...register("ticketsUrl", { required: "Enter the tickets URL." })}
                    />
                    {renderFieldError(errors.ticketsUrl?.message)}
                </div>

                <div className="event-form-field">
                    <label htmlFor="type">Type *</label>
                    <input
                        disabled={isWorking}
                        type="text"
                        id="type"
                        {...register("type", { required: "Enter an event type." })}
                    />
                    {renderFieldError(errors.type?.message)}
                </div>

                <div className="event-form-field">
                    <label htmlFor="startDatetime">Starts *</label>
                    <input
                        disabled={isWorking}
                        type="datetime-local"
                        id="startDatetime"
                        {...register("startDatetime", { required: "Select a start date and time." })}
                    />
                    {renderFieldError(errors.startDatetime?.message)}
                </div>

                <footer className="event-form-actions">
                    <button type="button" onClick={onCloseModal} disabled={isWorking}>
                        Cancel
                    </button>
                    <button type="submit" className="event-form-submit" disabled={isWorking}>
                        {isWorking
                            ? "Saving…"
                            : isEditSession
                                ? "Save changes"
                                : "Create event"}
                    </button>
                </footer>
            </form>
        </div>
    );
}

export default CreateEventForm;
